import { t } from '@lingui/core/macro';

/**
 * PK_CFIID is an institution's published OCIF identifier: three uppercase letters then five digits
 * (e.g. DBD00001). It is stored in the institution org unit's `code`.
 *
 * The backend enforces exactly this shape in `InstitutionOrgUnitCodeValidator` and rejects the save
 * otherwise, so the form checks it up front rather than surfacing a 400 after the fact. Keep the two
 * in step: the format is deliberately kept there because PK_CFIID is consumed outside the codebase
 * (historical data migrated from the legacy system, external reporting), not because anything here
 * derives from it.
 */
// The symbols here keep the PK_ prefix because that is the analytics column name
// (`FinancialInstitution.PK_CFIID`). USER-FACING copy says "CFIID": the PK_ is internal parlance.
export const PK_CFIID_REGEX = /^[A-Z]{3}[0-9]{5}$/;

/** The same shape as a react-input-mask pattern: `a` is a letter, `9` is a digit. */
export const PK_CFIID_MASK = 'aaa99999';

export const PK_CFIID_EXAMPLE = 'DBD00001';

/**
 * Returns a localized error message, or null when `value` is acceptable.
 *
 * An EMPTY value is acceptable here; requiredness is a separate concern, carried by the schema's
 * `required` list or `akc:requiredIfVisible`. Reporting both from one place would double up the
 * message on an untouched field.
 */
export function getPkCfiidError(value: unknown): string | null {
  if (value == null || value === '') return null;

  return PK_CFIID_REGEX.test(String(value))
    ? null
    : t`Must be 3 uppercase letters followed by 5 digits (e.g. DBD00001).`;
}
